import { roundMoneyPreview } from "@/features/currencies/utils"

/**
 * Pure helpers for the seat inputs on the invoice create/edit forms.
 *
 * The course line's Price field is the **per-seat unit price**: the
 * backend derives each course's total as `round(unit_price × seats)` and
 * sums the courses into the invoice amount, so the form sends the unit
 * verbatim and never rescales it. `scaleBySeats` therefore no longer
 * feeds the price default — it computes a DISPLAY-only "× N seats = <total>"
 * hint beside the Price input (see `CoursePriceFields`). Framework-agnostic
 * so both stay unit-testable in isolation.
 *
 * The seat count is NOT part of the course label — it renders as a
 * dedicated "Seats: N" line on the customer surfaces, sourced from the
 * stored `Invoice.seats`.
 */

/**
 * Parse a seats form value (a text input) into a whole number >= 1.
 *
 * Any non-positive / non-integer / blank value falls back to `1`, so the
 * arithmetic helpers never produce `NaN` while the rep is mid-edit; the
 * Zod schema is what surfaces the validation error for a bad value.
 */
export function parseSeats(raw: string): number {
  const n = Number.parseInt(raw, 10)
  return Number.isFinite(n) && n >= 1 ? n : 1
}

/**
 * Scale a per-seat unit price into the order **total** for `seats` seats.
 *
 * DISPLAY-only now: the Price field holds the per-seat unit and the backend
 * derives the total, so this is used solely to render the read-only
 * "× N seats = <total>" hint beside the Price input — it never writes back
 * into the form's price.
 *
 * Single-seat (`seats <= 1`) returns the unit price verbatim — the
 * headline regression guard, so a default-seat invoice keeps its exact
 * price string. For `seats > 1` the unit is rounded to whole major units
 * (billing precision) and multiplied. A blank or non-numeric unit price is
 * returned unchanged (the indicative-cohort "no quote" case, where the
 * price field stays empty).
 */
export function scaleBySeats(unitPrice: string, seats: number): string {
  if (unitPrice.trim() === "") return ""
  if (seats <= 1) return unitPrice
  // Reuse the shared half-up money rounder (mirrors the server's
  // round_money); it returns null for a non-numeric value, which we pass
  // through unchanged.
  const rounded = roundMoneyPreview(unitPrice)
  if (rounded === null) return unitPrice
  return String(rounded * seats)
}
